import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Utensils, Plus, Trash2, ShieldAlert, Download, Eye, Share2, CheckCircle2 } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const GOALS = [["balanced", "تغذية متوازنة"], ["maintain", "حفاظ على الوزن"], ["lose", "إنقاص الوزن"], ["gain", "زيادة الوزن"]];
const ACTIVITY = [["low", "خفيف"], ["moderate", "متوسط"], ["high", "عالٍ"]];
const PDF_NAME = "سهول_جنين_الدليل_الغذائي.pdf";

export default function Nutrition() {
  const { user } = useAuth();
  const { add } = useCart();
  const [f, setF] = useState({ weight: "", height: "", age: "", goal: "balanced", activity: "moderate", conditions: "", allergies: "", preferred: "", disliked: "", consent: false });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [saved, setSaved] = useState([]);

  const set = (k, v) => setF({ ...f, [k]: v });

  const loadSaved = () => { if (user) api.get("/my/nutrition").then((r) => setSaved(r.data)).catch(() => {}); };
  useEffect(() => { loadSaved(); }, [user]);

  const generate = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/assistant/nutrition", {
        ...f, weight: f.weight ? Number(f.weight) : null, height: f.height ? Number(f.height) : null,
        age: f.age ? Number(f.age) : null, consent: f.consent && !!user,
      });
      setResult(r.data);
      if (r.data.saved) { toast.success("حُفظ الجدول في حسابك"); loadSaved(); }
    } catch { toast.error("تعذّر توليد الجدول"); }
    setBusy(false);
  };

  const fetchPdfBlob = async () => {
    const r = await api.post("/nutrition/guide-pdf",
      { plan: result.plan, products: (result.products || []).map((p) => p.sku) },
      { responseType: "blob" });
    return new Blob([r.data], { type: "application/pdf" });
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = PDF_NAME; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("تم إنشاء دليلك بنجاح");
    } catch { toast.error("تعذّر إنشاء الملف"); }
    setPdfBusy(false);
  };

  const previewPdf = async () => {
    setPdfBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("تعذّر إنشاء الملف"); }
    setPdfBusy(false);
  };

  const sharePdf = async () => {
    setPdfBusy(true);
    try {
      const blob = await fetchPdfBlob();
      const file = new File([blob], PDF_NAME, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "دليلي الغذائي من سهول جنين" });
      } else {
        await navigator.clipboard.writeText("دليلي الغذائي من سهول جنين — أنشئه من صفحة الجدول الغذائي");
        toast.success("تم النسخ — مشاركة الملفات غير مدعومة في هذا المتصفح");
      }
    } catch (e) { if (e && e.name !== "AbortError") toast.error("تعذّرت المشاركة"); }
    setPdfBusy(false);
  };

  const deleteData = async () => {
    await api.delete("/my/nutrition");
    setSaved([]); toast.success("حُذفت بياناتك الصحية");
  };

  return (
    <div className="min-h-screen pt-24">
      <div className="relative overflow-hidden bg-olive-deep py-14 text-ivory">
        <div className="tatreez-bg absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <Utensils className="mx-auto mb-3 h-9 w-9 text-earth" />
          <h1 className="font-display text-3xl font-bold sm:text-4xl">الجدول الغذائي المخصص</h1>
          <p className="mt-3 text-ivory/75">جدول أسبوعي إرشادي يراعي هدفك وحساسيتك، مع منتجات مقترحة من المتجر — ودليل PDF فاخر يمكنك تنزيله.</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-6 flex items-start gap-2 rounded-2xl bg-earth/10 p-4 text-sm text-ink/70">
          <ShieldAlert className="h-5 w-5 shrink-0 text-earth" />
          <span>هذا الجدول إرشادي عام ولا يغني عن استشارة الطبيب أو أخصائي التغذية. لا تُحفظ بياناتك الصحية إلا بموافقتك الصريحة، ويمكنك حذفها متى شئت.</span>
        </div>

        <div className="grid gap-8 lg:grid-cols-[360px_1fr]">
          <div className="h-fit rounded-3xl border border-olive/10 bg-white p-6">
            <div className="grid grid-cols-3 gap-3">
              <Field label="الوزن (كغ)" value={f.weight} onChange={(v) => set("weight", v)} type="number" testid="nut-weight" />
              <Field label="الطول (سم)" value={f.height} onChange={(v) => set("height", v)} type="number" />
              <Field label="العمر" value={f.age} onChange={(v) => set("age", v)} type="number" />
            </div>
            <Sel label="الهدف" value={f.goal} onChange={(v) => set("goal", v)} options={GOALS} testid="nut-goal" />
            <Sel label="مستوى النشاط" value={f.activity} onChange={(v) => set("activity", v)} options={ACTIVITY} />
            <Field label="حالات صحية (اختياري)" value={f.conditions} onChange={(v) => set("conditions", v)} />
            <Field label="حساسية (تُستبعد تلقائياً)" value={f.allergies} onChange={(v) => set("allergies", v)} testid="nut-allergies" placeholder="مثال: سمسم، غلوتين" />
            <Field label="أطعمة مفضلة" value={f.preferred} onChange={(v) => set("preferred", v)} />
            <Field label="أطعمة غير مفضلة" value={f.disliked} onChange={(v) => set("disliked", v)} />
            {user && (
              <label className="mt-2 flex items-start gap-2 text-sm text-ink/70">
                <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1 accent-olive" data-testid="nut-consent" />
                أوافق على حفظ بياناتي الصحية في حسابي.
              </label>
            )}
            {!user && <p className="mt-2 text-xs text-ink/45"><Link to="/login" className="text-olive">سجّل الدخول</Link> لحفظ جدولك.</p>}
            <button data-testid="nut-generate" onClick={generate} disabled={busy} className="btn-shine mt-4 w-full rounded-full bg-olive py-3.5 font-semibold text-ivory disabled:opacity-60">
              {busy ? "يجهّز جدولك..." : "أنشئ الجدول"}
            </button>
          </div>

          <div>
            {busy && <div className="py-16 text-center text-ink/50">يجهّز أخصائي التغذية جدولك...</div>}
            {result && result.plan?.days?.length > 0 && (
              <>
                <div data-testid="guide-ready" className="mb-5 rounded-3xl border border-olive/20 bg-olive/[0.05] p-5">
                  <div className="flex items-center gap-2.5">
                    <CheckCircle2 className="h-6 w-6 shrink-0 text-olive" />
                    <div className="font-display text-lg font-bold text-olive-dark">تم إعداد دليلك الغذائي</div>
                  </div>
                  <p className="mt-1 text-sm text-ink/55">حمّله كملف PDF مصمم بهوية سهول جنين — غلاف، جدول أسبوعي، طرق استخدام، وتنبيهات.</p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    <button data-testid="guide-download" onClick={downloadPdf} disabled={pdfBusy}
                      className="btn-shine flex items-center gap-2 rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-ivory transition-colors hover:bg-olive-dark disabled:opacity-60">
                      <Download className="h-4 w-4" /> {pdfBusy ? "يجهّز الملف..." : "تنزيل الدليل PDF"}
                    </button>
                    <button data-testid="guide-preview" onClick={previewPdf} disabled={pdfBusy}
                      className="flex items-center gap-2 rounded-full border border-olive/25 px-5 py-2.5 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5 disabled:opacity-60">
                      <Eye className="h-4 w-4" /> معاينة الدليل
                    </button>
                    <button data-testid="guide-share" onClick={sharePdf} disabled={pdfBusy}
                      className="flex items-center gap-2 rounded-full border border-olive/25 px-5 py-2.5 text-sm font-semibold text-olive-dark transition-colors hover:bg-olive/5 disabled:opacity-60">
                      <Share2 className="h-4 w-4" /> مشاركة
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-3xl border border-olive/10 bg-white">
                  <table data-testid="nutrition-table" className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="bg-olive/5 text-ink/70">
                        <th className="p-3 text-right">اليوم</th><th className="p-3 text-right">فطور</th><th className="p-3 text-right">غداء</th><th className="p-3 text-right">عشاء</th><th className="p-3 text-right">خفيف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.plan.days.map((d, i) => (
                        <tr key={i} className="border-t border-olive/10 align-top">
                          <td className="p-3 font-semibold text-olive-dark">{d.day}</td>
                          <td className="p-3 text-ink/70">{d.breakfast}</td>
                          <td className="p-3 text-ink/70">{d.lunch}</td>
                          <td className="p-3 text-ink/70">{d.dinner}</td>
                          <td className="p-3 text-ink/70">{d.snacks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {result.plan.note && <p className="mt-3 text-sm text-ink/55">{result.plan.note}</p>}

                {result.products?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="mb-3 font-display text-lg font-bold text-ink">منتجات مقترحة لجدولك</h3>
                    <div className="space-y-2">
                      {result.products.map((p) => (
                        <div key={p.sku} className="flex items-center gap-3 rounded-2xl border border-olive/10 bg-white p-3">
                          <img src={p.image} alt={p.name_ar} className="h-14 w-14 rounded-xl object-cover" />
                          <div className="flex-1"><div className="font-medium text-ink">{p.name_ar}</div><div className="text-sm text-olive">{fmtPrice(p.price)}</div></div>
                          <button data-testid={`nut-add-${p.sku}`} onClick={() => add({ sku: p.sku, slug: p.slug, name_ar: p.name_ar, price: p.price, images: { main: p.image } })} className="flex h-10 items-center gap-1 rounded-full bg-olive px-4 text-sm text-ivory"><Plus className="h-4 w-4" /> أضف</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="mt-4 text-xs text-ink/45">{result.disclaimer}</p>
              </>
            )}
            {!busy && !result && <div className="rounded-3xl border border-dashed border-olive/20 py-20 text-center text-ink/50">املأ بياناتك واضغط «أنشئ الجدول».</div>}

            {saved.length > 0 && (
              <div className="mt-8 flex items-center justify-between rounded-2xl border border-olive/10 bg-white p-4">
                <span className="text-sm text-ink/60">لديك {saved.length} جدول محفوظ في حسابك.</span>
                <button data-testid="nut-delete" onClick={deleteData} className="flex items-center gap-1.5 text-sm text-palred"><Trash2 className="h-4 w-4" /> حذف بياناتي</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", placeholder, testid }) => (
  <div className="mt-3">
    <label className="mb-1 block text-xs text-ink/60">{label}</label>
    <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-olive/20 px-3 py-2.5 text-sm outline-none focus:border-olive" />
  </div>
);
const Sel = ({ label, value, onChange, options, testid }) => (
  <div className="mt-3">
    <label className="mb-1 block text-xs text-ink/60">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-olive/20 px-3 py-2.5 text-sm outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
